import { DevincoConnectUserData } from './../auth/interfaces';
import { Injectable, inject } from '@angular/core';
import { Country } from '@shared/constants/country';
import { map, Observable, switchMap, tap } from 'rxjs';
import { GetRowsResponse, RestApiService } from './rest-api.service';
import { UserDomain } from '@shared/interfaces/user-domain';
import { UserDto } from '@shared/interfaces/user.model';
import { Entity } from '@shared/constants';
import { OidcSecurityService } from 'angular-auth-oidc-client';

@Injectable({ providedIn: 'root' })
export class UserMetadataService {
  private api = inject(RestApiService);
  private oidcService = inject(OidcSecurityService);

  private userCountry: Country;
  private clientName: string;
  private userDomains: UserDomain[];
  private lastReleaseVersion: string;
  private userData: Pick<UserDto, 'UserDescription'>;

  getUserCountry(): Country {
    return this.userCountry;
  }

  getClientName(): string {
    return this.clientName;
  }

  getUserDomains(): UserDomain[] {
    return this.userDomains;
  }

  getUserData(): Partial<UserDto> {
    return this.userData;
  }

  getLastReleaseVersion(): string {
    return this.lastReleaseVersion;
  }

  hasSkiDomain(): boolean {
    return !!this.userDomains.find(domain => domain.DomainID === 2);
  }

  hasVehicleTrackingDomain(): boolean {
    return (
      !!this.userDomains.find(domain => domain.DomainID === 1) ||
      !this.userDomains.length
    );
  }

  getDomainById(id: number): UserDomain | undefined {
    return this.userDomains.find(domain => domain.DomainID === id);
  }

  fetchUserCountry(): Observable<Country> {
    return this.api.getCustom<{ Country: Country }>('getUserCountry').pipe(
      tap(({ Country }) => (this.userCountry = Country)),
      map(({ Country }) => Country)
    );
  }

  fetchUserData(): Observable<GetRowsResponse> {
    return this.oidcService.getUserData().pipe(
      map((userData: DevincoConnectUserData) => userData.email),
      switchMap(email =>
        this.api
          .getData<
            UserDto[]
          >({ entity: Entity.User, fields: ['UserDescription', 'Username'], filterModel: { UserName: { filterType: 'text', type: 'equals', filter: email } } })
          .pipe(
            tap(resp => {
              this.userData = resp.data?.[0];
            })
          )
      )
    );
  }

  fetchUserDomains(): Observable<UserDomain[]> {
    return this.api
      .getCustom<UserDomain[]>('User/getdomains')
      .pipe(tap(resp => (this.userDomains = resp)));
  }

  fetchLastDbReleaseVersion(): Observable<string> {
    return this.api
      .getCustom<string>('Version/web', undefined, {
        responseType: 'text',
      })
      .pipe(tap(resp => (this.lastReleaseVersion = resp.toString())));
  }

  fetchClientName(): Observable<string> {
    return this.api.getCustom<{ ClientName: string }>('getClientName').pipe(
      tap(({ ClientName }) => (this.clientName = ClientName)),
      map(({ ClientName }) => ClientName)
    );
  }
}
